import { useRTKInfo } from "@/api/information";
import { useUpdates } from "@/api/update";
import { POIType, RtkMap } from "@/assets/RtkMap";
import { AppShell } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CircularProgressBar } from "@/components/ui/circularprogress";
import { CarouselComponent } from "@/pages/home/sections/CarouselComponent";
import CustomLink from "@/pages/home/sections/CustomLink";
import { AwardsCarousel } from "@/pages/information/rtk/sections/AwardsCarousel";
import { Expenditures } from "@/pages/information/rtk/sections/ExpendituresSection";
import { MealsSection } from "@/pages/information/rtk/sections/MealsSection";
import { PartnersSection } from "@/pages/information/rtk/sections/PartnersSection";
import { SliderSection } from "@/pages/information/rtk/sections/SliderSection";
import { ValuesSection } from "@/pages/information/components/ValuesSection";
import { VolunteerSection } from "@/pages/information/rtk/sections/VolunteerSection";
import { UpdateCard } from "@/pages/program/sections/update-card";
import { UpdateType } from "@/types/programs";
import { getLoginURL } from "@/utils/auth";
import allMarketsDetails from "@/utils/markets";
import allGovDetails from "@/utils/rtk.ts";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Header } from "../header";
import { MediaCarousel } from "./sections/MediaCarousel";

const LocationIcon = () => (
  <svg className="h-8 w-8" viewBox="0 0 29 35" fill="none">
    <path
      d="M14.4376 1.6665C7.01641 1.6665 1 7.68291 1 15.1041C1 22.9521 9.30807 30.327 12.858 33.1199C13.7847 33.8487 15.088 33.8487 16.016 33.1199C19.5672 30.3257 27.874 22.9521 27.874 15.1041C27.8753 7.68163 21.8588 1.6665 14.4376 1.6665ZM14.4376 20.0717C11.5676 20.0717 9.24123 17.7454 9.24123 14.8754C9.24123 12.0053 11.5676 9.67895 14.4376 9.67895C17.3077 9.67895 19.634 12.0053 19.634 14.8754C19.634 17.7454 17.3077 20.0717 14.4376 20.0717Z"
      stroke="#232329"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RtkPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data } = useRTKInfo({ lang: i18n.language });
  const [isScrolled, setIsScrolled] = useState(false);
  const { data: updatesData } = useUpdates({ lang: i18n.language });
  const headerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>("our-services");
  const [mentions, setMentions] = useState([]);
  const [awards, setAwards] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState("22,858");
  const [cbos, setCbos] = useState(42);
  const [availableProducts, setAvailableProducts] = useState<number>(1405738);

  const formattedProducts = availableProducts.toLocaleString(); // "1,405,738"

  const [POI, setPOI] = useState<string | null>(null);
  const [services, setServices] = useState<string | null>(null);

  const handlePOIChange = useCallback(
    (POI: { name: string; code: string; type: POIType } | null) => {
      setPOI(POI ? POI.name : null);
      if (POI && POI.type === POIType.Governorate) {
        const govDetails = allGovDetails.find((gov) => gov.code === POI.code);
        if (govDetails) {
          setBeneficiaries(govDetails.beneficiaries);
          setCbos(govDetails.cbos);
          setServices(i18n.language === "ar" ? govDetails.servicesAr : govDetails.services);
        }
      } else if (POI && POI.type === POIType.Market) {
        const marketDetails = allMarketsDetails.find((market) => market.code === POI.code);
        if (marketDetails) {
          setBeneficiaries(marketDetails.beneficiaries);
          setAvailableProducts(marketDetails.products);
        }
      } else {
        setBeneficiaries("22,858");
        setCbos(42);
        setServices(null);
        setAvailableProducts(1405738);
      }
    },
    [],
  );

  const handleTabClick = useCallback(
    (tab: string) => {
      setActiveTab(tab);
    },
    [setActiveTab],
  );

  const aboutInfo = data?.about?.[0];

  useEffect(() => {
    if (aboutInfo) {
      const mentionsMedia = aboutInfo.documents.filter(
        (doc: { file_type: string }) => doc.file_type === "mentions_logo",
      );
      setMentions(mentionsMedia);
    }
  }, [aboutInfo]);

  useEffect(() => {
    if (aboutInfo) {
      const awardsMedia = aboutInfo.documents.filter(
        (doc: { file_type: string }) => doc.file_type === "awards_logo",
      );
      setAwards(awardsMedia);
    }
  }, [aboutInfo]);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setIsScrolled(rect.bottom <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const updates: UpdateType[] =
    updatesData?.filter((update: { is_aboutus: any }) => update.is_aboutus) || [];

  return (
    <>
      <Helmet>
        <title>{t("aboutPage.metaTitle")}</title>
        <meta name="description" content={t("aboutPage.metaDescription")} />
        <meta name="keywords" content={t("aboutPage.metaKeywords")} />
        <link rel="canonical" href="https://efb.eg/rtk" />
        <meta property="og:title" content={t("aboutPage.ogTitle")} />
        <meta property="og:description" content={t("aboutPage.ogDescription")} />
        <meta property="og:url" content="https://efb.eg/rtk" />
        <meta property="og:site_name" content={t("siteName")} />
      </Helmet>

      <div ref={headerRef}>
        <Header
          title={aboutInfo?.title}
          scrollHref="#rtk-map"
          picture="aboutUs.jpg"
          light
          aboutUs
        />
      </div>

      <AppShell
        navbarClass={clsx(
          "fixed top-0 -z-10 w-full bg-[#CDCFC9] opacity-0 transition-opacity duration-300 ease-in-out",
          { "opacity-100 z-50": isScrolled },
        )}
      />

      <section className="mx-4 my-16 md:mx-auto md:my-24 md:px-20 lg:px-80">
        <p className="text-2xl">{aboutInfo?.whoIsEFB}</p>
        <h4 className="text-2xl">{aboutInfo?.description}</h4>
      </section>

      <section
        id="rtk-map"
        className="relative mx-6 my-16 rounded-2xl bg-[#E2DDD7] md:my-24 md:grid md:grid-cols-2"
      >
        <div className="flex flex-col justify-between px-8 pb-8 pt-8 md:pl-8 md:pt-16">
          <h1 className="text-2xl font-semibold md:text-4xl lg:text-5xl">
            {t("aboutPage.coverage")}
            <br />
            {t("aboutPage.period")}
          </h1>
          <div className="block md:hidden">
            <div className="absolute right-[48px] top-[130px] z-40 rounded-2xl bg-[#EDEAE4] p-2 rtl:left-[48px] rtl:right-auto">
              <div className="w-24 text-center text-sm text-[#232329]">{t("aboutPage.hover")}</div>
            </div>
            <div className="h-full px-2 py-8">
              <RtkMap onPOIHover={handlePOIChange} activeTab={activeTab} />
            </div>
          </div>
          <div className="mb-6 mt-4 flex w-full rounded-md border border-[#EDEAE4] bg-[#E2DDD7] text-sm md:text-base">
            <button
              onClick={() => handleTabClick("our-services")}
              className={`w-1/2 cursor-pointer py-3 text-center ${
                activeTab === "our-services"
                  ? "rounded-md bg-[#232329] font-bold text-[#EDEAE4]"
                  : ""
              }`}
            >
              {t("aboutPage.ourServices")}
            </button>
            <button
              onClick={() => handleTabClick("supermarket-projects")}
              className={`w-1/2 cursor-pointer py-3 text-center ${
                activeTab === "supermarket-projects"
                  ? "rounded-md bg-[#232329] font-bold text-[#EDEAE4]"
                  : ""
              }`}
            >
              {t("aboutPage.supermarketProjects")}
            </button>
          </div>
          <div className="flex min-h-20 flex-row justify-start gap-2 py-4">
            <LocationIcon />
            <div className="flex flex-col">
              {activeTab === "our-services" ? (
                <h6>{POI ?? `${allGovDetails.length} ${t("aboutPage.governorates")}`}</h6>
              ) : (
                <>
                  <h6>{POI ?? `${allMarketsDetails.length} ${t("aboutPage.organizations")} .`}</h6>
                  <h6>
                    {POI
                      ? ""
                      : `${t("aboutPage.across")} ${allGovDetails.length} ${t("aboutPage.governorate")}`}
                  </h6>
                </>
              )}

              {activeTab === "our-services" && (
                <h6>{POI ? services : `5 ${t("aboutPage.services")}`}</h6>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative flex h-48 flex-col justify-end rounded-md bg-[#EDEAE4] p-4 lg:p-8">
              <svg
                className="absolute bottom-0 right-0"
                width="76"
                height="127"
                viewBox="0 0 76 127"
                fill="none"
              >
                <g opacity="0.1">
                  <path
                    d="M86 57.5164V105.477C86 115.104 82.3183 124.371 75.7011 131.4C69.0839 138.429 60.0261 142.694 50.3631 143.332V95.3621C50.3566 85.7342 54.0363 76.4647 60.6551 69.4357C67.2738 62.4067 76.3354 58.1452 86 57.5164Z"
                    stroke="#0A5849"
                  />
                  <path
                    d="M0.0554306 0.405052C0.0554306 0.405052 0.0554306 2.60236 0.0554306 3.69638C0.046104 25.1503 -0.205714 80.9916 0.447148 102.427C0.63368 108.555 2.87206 115.073 5.82859 120.608C13.6163 135.173 30.8239 144.324 44.8511 142.405C44.8511 118.3 46.1288 59.6953 44.4593 35.6547C43.0604 15.5174 20.434 -0.902209 0.0554306 0.405052Z"
                    stroke="#0A5849"
                  />
                  <path
                    d="M81.9336 11.0486V31.4456C81.9336 46.5486 69.6038 54.1697 54.4574 55.1617V34.7647C54.4574 19.6431 66.7592 12.0499 81.9336 11.0579"
                    stroke="#0A5849"
                  />
                </g>
              </svg>
              <h2 className="break-words text-3xl md:text-4xl lg:text-5xl">{beneficiaries}</h2>
              <p className="md:text-lg">{t("aboutPage.beneficiaries")}</p>
            </div>
            {activeTab === "our-services" && (
              <div className="flex h-48 flex-col justify-end rounded-md border border-[#EDEAE4] p-4 lg:p-8">
                <h2 className="break-words text-3xl md:text-4xl lg:text-5xl">{cbos}</h2>
                <p className="md:text-lg">{t("aboutPage.cbos")}</p>
              </div>
            )}
            {activeTab === "supermarket-projects" && (
              <div className="flex h-48 flex-col justify-end rounded-md border border-[#EDEAE4] p-4 lg:p-8">
                <h2 className="break-words text-3xl md:text-4xl lg:text-5xl">
                  {formattedProducts}
                </h2>
                <p className="md:text-lg">{t("aboutPage.availableProducts")}</p>
              </div>
            )}
          </div>
        </div>
        <div className="hidden md:block">
          {activeTab === "our-services" && (
            <div className="absolute right-[48px] top-[24px] z-40 rounded-2xl bg-[#EDEAE4] p-4 rtl:left-[48px] rtl:right-auto">
              <div className="w-24 text-center text-sm text-[#232329]">{t("aboutPage.hover")}</div>
            </div>
          )}
          <div className="h-full px-2 py-8">
            <RtkMap onPOIHover={handlePOIChange} activeTab={activeTab} />
          </div>
        </div>
      </section>

      <SliderSection />
      <MealsSection />

      <section className="my-16 p-6">
        <h5 className="text-2xl font-semibold md:text-5xl">
          {t("aboutPage.expenditures")}:<br />
          {t("aboutPage.period")}
        </h5>
        <Expenditures />
      </section>

      <section className="mx-6 my-16 flex flex-row gap-8 md:my-16">
        <div className="hidden min-h-[684px] w-full flex-1 md:block">
          <img
            className="h-full w-full rounded-lg object-cover object-center"
            src="/satisfaction.png"
            alt={t("aboutPage.customerSatisfaction")}
          />
        </div>
        <div className="flex-1 rounded-lg bg-[#E4E1D8] p-8">
          <h2 className="my-10 text-2xl font-semibold md:text-5xl">
            {t("aboutPage.customerSatisfaction")}:<br />
            {t("aboutPage.period")}
          </h2>

          <p className="font-bold">{t("aboutPage.donorsSatisfaction")}</p>
          <ProgressPrimitive.Root
            className="relative my-2 h-10 w-full overflow-hidden rounded-[12px] bg-[#EDEAE4] rtl:scale-x-[-1] rtl:transform"
            aria-label={t(
              "aboutPage.donorsSatisfactionProgressLabel",
              "Donors satisfaction progress bar",
            )}
          >
            {/* INSERT HERE SATISFACTION RATES */}
            <ProgressPrimitive.Indicator
              className="custom-duration ease flex h-full flex-1 items-center justify-end rounded-[12px] bg-[#0A5849] transition-transform rtl:justify-start"
              style={{ transform: `translateX(-${100 - 98.52}%)` }}
            >
              <p className="text-md pr-2 text-left text-[#EDEAE4] rtl:scale-x-[-1] rtl:pl-2">
                98.18%
              </p>
            </ProgressPrimitive.Indicator>
          </ProgressPrimitive.Root>
          <div className="mt-6 rounded-lg bg-[#EDEAE4] p-6">
            <div className="flex">
              <div className="flex-1 md:pr-12">
                <p className="text-lg font-bold">{t("aboutPage.beneficiariesSatisfaction")}</p>
                <div className="my-8 block md:hidden">
                  <CircularProgressBar percentage={94.97} />
                </div>
                <p className="mt-4 text-lg font-bold">
                  {t("aboutPage.beneficiariesDissatisfactionReasonsTitle")}
                </p>
                <div className="my-2 inline-flex items-start gap-2">
                  <svg
                    className="mt-1 h-6 min-h-6 w-6 min-w-6 flex-shrink-0"
                    viewBox="0 0 24 25"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.54546 0.332031C8.14795 0.332031 8.63636 0.820448 8.63636 1.42294V2.51385H15.1818V1.42294C15.1818 0.820448 15.6702 0.332031 16.2727 0.332031C16.8752 0.332031 17.3636 0.820448 17.3636 1.42294V2.51562C17.8823 2.51919 18.3357 2.5299 18.7293 2.56206C19.3425 2.61216 19.9061 2.71969 20.4356 2.98946C21.2567 3.40781 21.9242 4.07537 22.3426 4.89644C22.6123 5.42589 22.7199 5.98957 22.77 6.60277C22.8182 7.19316 22.8182 7.9179 22.8182 8.79604V18.0498C22.8182 18.928 22.8182 19.6527 22.77 20.2431C22.7199 20.8563 22.6123 21.42 22.3426 21.9494C21.9242 22.7705 21.2567 23.4381 20.4356 23.8564C19.9061 24.1262 19.3425 24.2337 18.7293 24.2838C18.1389 24.3321 17.4141 24.332 16.536 24.332H7.28219C6.40406 24.332 5.67931 24.3321 5.08892 24.2838C4.47572 24.2337 3.91204 24.1262 3.38259 23.8564C2.56152 23.4381 1.89397 22.7705 1.47561 21.9494C1.20584 21.42 1.09831 20.8563 1.04821 20.2431C0.999975 19.6527 0.999987 18.928 1 18.0498V8.79607C0.999987 7.91792 0.999975 7.19316 1.04821 6.60277C1.09831 5.98957 1.20584 5.42589 1.47561 4.89644C1.89397 4.07537 2.56152 3.40781 3.38259 2.98946C3.91204 2.71969 4.47572 2.61216 5.08892 2.56206C5.48253 2.5299 5.93586 2.51919 6.45455 2.51562V1.42294C6.45455 0.820448 6.94296 0.332031 7.54546 0.332031ZM6.45455 4.69759C5.96852 4.701 5.58687 4.71046 5.26659 4.73663C4.78832 4.77571 4.54373 4.84654 4.37311 4.93347C3.96258 5.14265 3.6288 5.47643 3.41962 5.88696C3.33269 6.05758 3.26186 6.30217 3.22278 6.78044C3.18267 7.27144 3.18182 7.90667 3.18182 8.84112V9.0593H20.6364V8.84112C20.6364 7.90667 20.6355 7.27144 20.5954 6.78044C20.5563 6.30217 20.4855 6.05758 20.3986 5.88696C20.1894 5.47643 19.8556 5.14265 19.4451 4.93347C19.2745 4.84654 19.0299 4.77571 18.5516 4.73663C18.2313 4.71046 17.8497 4.701 17.3636 4.69759V5.78658C17.3636 6.38907 16.8752 6.87749 16.2727 6.87749C15.6702 6.87749 15.1818 6.38907 15.1818 5.78658V4.69567H8.63636V5.78658C8.63636 6.38907 8.14795 6.87749 7.54546 6.87749C6.94296 6.87749 6.45455 6.38907 6.45455 5.78658V4.69759ZM20.6364 11.2411H3.18182V18.0048C3.18182 18.9392 3.18267 19.5744 3.22278 20.0654C3.26186 20.5437 3.33269 20.7883 3.41962 20.9589C3.6288 21.3695 3.96258 21.7032 4.37311 21.9124C4.54373 21.9993 4.78832 22.0702 5.26659 22.1092C5.75759 22.1494 6.39282 22.1502 7.32727 22.1502H16.4909C17.4254 22.1502 18.0606 22.1494 18.5516 22.1092C19.0299 22.0702 19.2745 21.9993 19.4451 21.9124C19.8556 21.7032 20.1894 21.3695 20.3986 20.9589C20.4855 20.7883 20.5563 20.5437 20.5954 20.0654C20.6355 19.5744 20.6364 18.9392 20.6364 18.0048V11.2411Z"
                      stroke="#232329"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="text-lg">
                    {t("aboutPage.beneficiariesDissatisfactionDeliveryReason")}
                  </p>
                </div>
                <div className="mb-2 inline-flex items-start gap-2">
                  <svg
                    className="mt-1 h-6 min-h-6 w-6 min-w-6 flex-shrink-0"
                    viewBox="0 0 23 27"
                    fill="none"
                  >
                    <g id="Location">
                      <path
                        id="Vector"
                        d="M11.3282 1.66602C5.76224 1.66602 1.24994 6.17832 1.24994 11.7442C1.24994 17.6302 7.48099 23.1614 10.1435 25.2561C10.8385 25.8027 11.8159 25.8027 12.5119 25.2561C15.1753 23.1604 21.4054 17.6302 21.4054 11.7442C21.4064 6.17736 16.8941 1.66602 11.3282 1.66602ZM11.3282 15.4699C9.17563 15.4699 7.43086 13.7252 7.43086 11.5727C7.43086 9.42012 9.17563 7.67535 11.3282 7.67535C13.4807 7.67535 15.2255 9.42012 15.2255 11.5727C15.2255 13.7252 13.4807 15.4699 11.3282 15.4699Z"
                        stroke="#232329"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                  <p className="text-lg">
                    {t("aboutPage.beneficiariesDissatisfactionLocationReason")}
                  </p>
                </div>
              </div>
              <div className="hidden md:block">
                <CircularProgressBar percentage={94.97} />
              </div>
            </div>
            <hr className="my-6 border-[#E2DDD7]" />
            <p className="text-lg">{t("aboutPage.beneficiariesDissatisfactionProgress")}</p>
          </div>
        </div>
      </section>

      <section className="my-16 p-6">
        <h1 className="text-2xl font-semibold md:text-5xl">
          {t("aboutPage.ourPartners")} {t("aboutPage.period")}
        </h1>
        <PartnersSection />
      </section>

      <section className="my-16 p-6">
        <h1 className="text-2xl font-semibold md:text-5xl">
          {t("aboutPage.volunteerPartners")}
          <br />
          {t("aboutPage.period")}
        </h1>
        <VolunteerSection />
      </section>

      <section className="mx-6 my-16 md:mx-6 md:my-16">
        <div className="mb-8 flex flex-col md:mb-16 md:flex-row md:items-center md:justify-between">
          <h2 className="mb-4 text-2xl font-semibold md:mb-0 md:text-5xl">
            {t("aboutPage.annual")}
          </h2>
          <div className="flex flex-col justify-end">
            <CustomLink
              to="/about-us"
              mobileText={t("aboutPage.ourapproach")}
              desktopText={t("aboutPage.approach")}
            />
          </div>
        </div>
        <div className="w-full">
          <CarouselComponent imagesMd={1} imagesLg={2}>
            {updates.map((update: UpdateType) => (
              <div
                key={`my-updates-${update.id}`}
                className="flex aspect-9/16 min-w-[250px] max-w-[250px] items-stretch md:min-w-[350px] md:max-w-[350px]"
              >
                <UpdateCard update={update} bgColor="#E4E1D8" showYearOnly={true} />
              </div>
            ))}
          </CarouselComponent>
        </div>
      </section>

      <section className="my-16 p-6">
        <h5 className="text-2xl font-semibold md:text-5xl">{t("aboutPage.values")}</h5>
        <ValuesSection />
      </section>

      <section className="mx-6 my-16 md:mx-6 md:my-16">
        <h2 className="mb-4 text-2xl font-bold md:text-4xl">{t("aboutPage.mentioned")}</h2>
        <div className="mb-8 md:mb-16">
          <MediaCarousel items={mentions} />
        </div>
      </section>

      <section className="mx-6 my-16 md:mx-6 md:my-32">
        <AwardsCarousel items={awards} />
      </section>

      <section className="mx-6 my-16 md:mx-6 md:my-16">
        <div className="rounded-lg bg-[#E4E1D8] p-8 md:px-40 md:py-16 lg:px-80">
          <h3 className="pb-8 text-xl font-bold md:text-[32px]">{t("aboutPage.believe")}</h3>
          <h4 className="pb-4 text-lg md:text-2xl">{t("aboutPage.everyone")}</h4>
          <h4 className="pb-8 text-lg md:text-2xl">{t("aboutPage.already")}</h4>
          <Link to={getLoginURL("")}>
            <Button className="px-10 py-4 text-base font-bold">{t("aboutPage.join")}</Button>
          </Link>
        </div>
      </section>
    </>
  );
};
